import { Injectable } from "@angular/core";
import { WeaponType, DefenceType, Comparator, Rarity, VendoringGearType } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { AdvancedConditions, CustomRule, DisplayOptions, Filter } from "../../models/filter-models";
import { GenerationHelperService } from "./generation-helper.service";
import { ItemDataService } from "../item-data.service";
import { RuleGeneratorService } from "./rule-generator.service";
import { poe1, poe2 } from "../../../game";
import { RuleId } from "../../models/rule-id";
import { PresetsService } from "../presets.service";
import { ClassType } from "../../data/poe2/classes";

type DefencePatternValue = 0 | 1 | 2; // 0 = must not have, 1 = must have, 2 = don't care
type DefencePattern = [DefencePatternValue, DefencePatternValue, DefencePatternValue]; // [Armour, Evasion, EnergyShield]

export interface DefenceRule {
  minArmour?: number;
  minEvasion?: number;
  minEnergyShield?: number;
}

const DEFENCE_TYPE_MASK: Record<DefenceType, number | undefined> = {
  [DefenceType.Armour]: 0b100,
  [DefenceType.Evasion]: 0b010,
  [DefenceType.EnergyShield]: 0b001,
  [DefenceType.ArmourEvasion]: 0b110,
  [DefenceType.ArmourEnergyShield]: 0b101,
  [DefenceType.EvasionEnergyShield]: 0b011,
  [DefenceType.All]: undefined, // handled specially
};

const DEFENCE_MASKS = Object.values(DEFENCE_TYPE_MASK).filter((mask): mask is number => mask !== undefined);

function maskMatchesPattern(pattern: DefencePattern, mask: number): boolean {
  const bits: [number, number, number] = [(mask >> 2) & 1, (mask >> 1) & 1, mask & 1];
  return pattern.every((p, idx) => p === 2 || p === bits[idx]);
}

function patternToRule([armour, evasion, es]: DefencePattern): DefenceRule {
  const convert = (v: DefencePatternValue) => v === 2 ? undefined : v === 1 ? 1 : 0;
  return {
    minArmour: convert(armour),
    minEvasion: convert(evasion),
    minEnergyShield: convert(es),
  };
}

export const STARTER_WEAPON_BASE_TYPES: { class: ClassType, baseTypes: string[] }[] = [
  { class: 'Warrior', baseTypes: ['Wooden Club'] },
  { class: 'Ranger', baseTypes: ['Crude Bow'] },
  { class: 'Huntress', baseTypes: ['Hardwood Spear', 'Leather Buckler'] },
  { class: 'Witch', baseTypes: ['Withered Wand', 'Rattling Sceptre'] },
  { class: 'Sorceress', baseTypes: ['Withered Wand', 'Voltaic Staff'] },
  { class: 'Mercenary', baseTypes: ['Makeshift Crossbow'] },
  { class: 'Monk', baseTypes: ['Wrapped Quarterstaff'] },
  { class: 'Druid', baseTypes: ['Changeling Talisman', 'Ashen Staff'] },
];

// Absolute area-level link bands for PoE1 Excellent/Good Socketed leveling gear.
// AL 1–24: Good 3L only (no Excellent). From 25+: Excellent = max, Good = max−1.
export interface LevelingLinkBand {
  minAreaLevel: number;
  maxAreaLevel: number | null;
  excellentLinks: number | null;
  goodLinks: number;
}

export const LEVELING_LINK_BANDS: LevelingLinkBand[] = [
  { minAreaLevel: 1, maxAreaLevel: 24, excellentLinks: null, goodLinks: 3 },
  { minAreaLevel: 25, maxAreaLevel: 34, excellentLinks: 4, goodLinks: 3 },
  { minAreaLevel: 35, maxAreaLevel: 49, excellentLinks: 5, goodLinks: 4 },
  { minAreaLevel: 50, maxAreaLevel: null, excellentLinks: 6, goodLinks: 5 },
];

export function getLevelingLinkBand(areaLevel: number): LevelingLinkBand {
  for (const band of LEVELING_LINK_BANDS) {
    if (areaLevel < band.minAreaLevel) continue;
    if (band.maxAreaLevel === null || areaLevel <= band.maxAreaLevel) return band;
  }
  return LEVELING_LINK_BANDS[LEVELING_LINK_BANDS.length - 1];
}

export function buildDefenceRules(defenceTypes: DefenceType[]): DefenceRule[] {
  const allowed = new Set<number>();

  if (defenceTypes.includes(DefenceType.All)) {
    DEFENCE_MASKS.forEach(mask => allowed.add(mask));
  } else {
    defenceTypes.forEach(type => {
      const mask = DEFENCE_TYPE_MASK[type];
      if (mask !== undefined) {
        allowed.add(mask);
      }
    });
  }

  // If nothing is selected, default to a permissive rule (don't filter by defences).
  if (!allowed.size) {
    return [{ minArmour: undefined, minEvasion: undefined, minEnergyShield: undefined }];
  }

  const patterns: { pattern: DefencePattern; covers: number[] }[] = [];
  const values: DefencePatternValue[] = [0, 1, 2];

  for (const armour of values) {
    for (const evasion of values) {
      for (const es of values) {
        const pattern: DefencePattern = [armour, evasion, es];
        const coverage = DEFENCE_MASKS.filter(mask => maskMatchesPattern(pattern, mask));
        if (!coverage.length) continue;

        const isValid = coverage.every(mask => allowed.has(mask));
        if (!isValid) continue;

        const allowedCoverage = coverage.filter(mask => allowed.has(mask));
        if (allowedCoverage.length) {
          patterns.push({ pattern, covers: allowedCoverage });
        }
      }
    }
  }

  patterns.sort((a, b) => b.covers.length - a.covers.length);

  let best: DefencePattern[] | null = null;
  const allowedSize = allowed.size;

  function backtrack(start: number, chosen: DefencePattern[], covered: Set<number>) {
    if (best && chosen.length >= best.length) return;
    if (covered.size === allowedSize) {
      best = chosen.slice();
      return;
    }

    for (let i = start; i < patterns.length; i++) {
      const { pattern, covers } = patterns[i];
      const newOnes = covers.filter(mask => !covered.has(mask));
      if (!newOnes.length) continue;

      newOnes.forEach(mask => covered.add(mask));
      chosen.push(pattern);
      backtrack(i + 1, chosen, covered);
      chosen.pop();
      newOnes.forEach(mask => covered.delete(mask));
    }
  }

  backtrack(0, [], new Set<number>());

  const chosenPatterns = best ?? [];
  return chosenPatterns.map(patternToRule);
}

@Injectable({
  providedIn: 'root'
})

export class LevelingGearGeneratorService {
  oneHandWeaponTypes: WeaponType[];

  constructor(private itemDataService: ItemDataService, private helper: GenerationHelperService, private ruleFormatter: RuleGeneratorService) {
    this.oneHandWeaponTypes = this.itemDataService.getOneHandWeaponTypes();
  }

  private splitWeaponClassesBySpecialTypes(weaponClasses: string[], ignoreLevelDifference: boolean): { special: string[], nonSpecial: string[] } {
    if (!ignoreLevelDifference) {
      return { special: [], nonSpecial: weaponClasses };
    }

    const specialWeaponTypes = PresetsService.specialWeaponTypes.map(wt => wt.toString());
    const special: string[] = [];
    const nonSpecial: string[] = [];

    weaponClasses.forEach(wc => {
      if (specialWeaponTypes.includes(wc)) {
        special.push(wc);
      } else {
        nonSpecial.push(wc);
      }
    });

    return { special, nonSpecial };
  }

  public generateLevelingGearExceptionsRules(filter: Filter): string {
    if (filter.showLevelingGear === false) return '';

    const minAreaLevel = filter.showLevelingGearMinAreaLevel ?? 0;
    const maxAreaLevel = filter.showLevelingGearMaxAreaLevel ?? 80;
    if (maxAreaLevel <= minAreaLevel) {
      return '';
    }

    let weaponRules = '';
    let is2hWeaponOnly = !filter.showLevelingGearWeaponClasses.includes('All') && !filter.showLevelingGearWeaponClasses.some(c => this.oneHandWeaponTypes.includes(c as WeaponType));

    if (filter.showLevelingGearWeapons) {
      if (poe2) {
        weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Normal,
          baseTypes: [...new Set(STARTER_WEAPON_BASE_TYPES.flatMap(c => c.baseTypes))],
          advancedConditions: new AdvancedConditions({
            minAreaLevel: 1,
            maxAreaLevel: 1,
          }),
        }), filter, 'Starter Weapons on The Riverbank', undefined, 'gear-leveling-weapons-normal') + '\n\n';
      }

      const weaponClasses = filter.showLevelingGearWeaponClasses.includes('All')
        ? this.itemDataService.getAllWeaponTypes()
        : filter.showLevelingGearWeaponClasses as string[];
      const weaponClassesWithoutQuivers = filter.showLevelingGearWeaponClasses.includes('All')
        ? this.itemDataService.getAllWeaponTypes().filter(c => c !== 'Quivers')
        : filter.showLevelingGearWeaponClasses.filter(c => c !== 'Quivers') as string[];

      // Generate Excellent Socketed rules (absolute link counts by area band)
      if (poe1 && filter.showLevelingGearExcellentSocketed &&
          (filter.showLevelingGearWeaponClasses.length > 1 || !filter.showLevelingGearWeaponClasses.includes('Quivers'))) {
        const filterItemClass = weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' ');
        for (const band of LEVELING_LINK_BANDS) {
          if (band.excellentLinks === null) continue;
          weaponRules += this.emitLinkCountRulesForBand({
            filter,
            band,
            minAreaLevel,
            maxAreaLevel,
            minLinks: band.excellentLinks,
            difference: filter.showLevelingGearExcellentSocketedDifference,
            equipmentType: EquipmentType.OtherGearLevelingExcellentSocketed,
            filterItemClass,
            ruleName: 'Excellent Socketed Leveling Weapons',
            alwaysRuleName: 'Leveling Weapons Excellent Socketed',
          });
        }
      }

      // Generate Good Socketed rules (absolute link counts by area band)
      if (poe1 && filter.showLevelingGearOtherSocketed &&
          (filter.showLevelingGearWeaponClasses.length > 1 || !filter.showLevelingGearWeaponClasses.includes('Quivers'))) {
        const filterItemClass = weaponClassesWithoutQuivers.map(c => `"${c}"`).join(' ');
        for (const band of LEVELING_LINK_BANDS) {
          weaponRules += this.emitLinkCountRulesForBand({
            filter,
            band,
            minAreaLevel,
            maxAreaLevel,
            minLinks: band.goodLinks,
            difference: filter.showLevelingGearOtherSocketedLevelDifference,
            equipmentType: EquipmentType.OtherGearLevelingGoodSocketed,
            filterItemClass,
            ruleName: 'Good Socketed Leveling Weapons',
            alwaysRuleName: 'Leveling Weapons Good Socketed',
          });
        }
      }

      // Generate Rare rules
      if (filter.showLevelingGearRare) {
        const { special: specialWeaponClasses, nonSpecial: nonSpecialWeaponClasses } = this.splitWeaponClassesBySpecialTypes(
          weaponClasses,
          (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes) ?? false
        );

        // Generate rules for special weapon types (always show, no level difference) - POE2 only
        if (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && specialWeaponClasses.length > 0) {
          const rareEquipmentType = (filter.showVendoringRares && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingRaresStyle
            : EquipmentType.OtherGearLevelingRare;
          weaponRules += this.ruleFormatter.generateEquipmentRule(rareEquipmentType, new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Rare,
            filterItemClass: specialWeaponClasses.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({}),
          }), filter, 'Rare Special Leveling Weapons') + '\n\n';
        }

        // Generate rules for non-special weapon types (with level difference)
        if (nonSpecialWeaponClasses.length > 0) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearRareLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            const rareEquipmentType = (filter.showVendoringRares && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
              ? EquipmentType.OtherGearLevelingRaresStyle
              : EquipmentType.OtherGearLevelingRare;
            weaponRules += this.ruleFormatter.generateEquipmentRule(rareEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Rare,
              filterItemClass: nonSpecialWeaponClasses.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({ maxAreaLevel: areaLevel, minDropLevel: effectiveMinDropLevel }),
            }), filter, 'Rare Leveling Weapons') + '\n\n';
          }
        }
      }

      // Generate Magic rules
      if (filter.showLevelingGearMagic) {
        const { special: specialWeaponClasses, nonSpecial: nonSpecialWeaponClasses } = this.splitWeaponClassesBySpecialTypes(
          weaponClasses,
          (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes) ?? false
        );

        // Generate rules for special weapon types (always show, no level difference) - POE2 only
        if (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && specialWeaponClasses.length > 0) {
          const magicEquipmentType = (filter.showVendoringMagics && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingMagicsStyle
            : EquipmentType.OtherGearLevelingMagic;
          weaponRules += this.ruleFormatter.generateEquipmentRule(magicEquipmentType, new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Magic,
            filterItemClass: specialWeaponClasses.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({}),
          }), filter, 'Magic Special Leveling Weapons') + '\n\n';
        }

        // Generate rules for non-special weapon types (with level difference)
        if (nonSpecialWeaponClasses.length > 0) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearMagicLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            const magicEquipmentType = (filter.showVendoringMagics && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
              ? EquipmentType.OtherGearLevelingMagicsStyle
              : EquipmentType.OtherGearLevelingMagic;
            weaponRules += this.ruleFormatter.generateEquipmentRule(magicEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Magic,
              filterItemClass: nonSpecialWeaponClasses.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({ maxAreaLevel: areaLevel, minDropLevel: effectiveMinDropLevel }),
            }), filter, 'Magic Leveling Weapons') + '\n\n';
          }
        }
      }

      // Generate Normal rules
      if (filter.showLevelingGearNormal) {
        const { special: specialWeaponClasses, nonSpecial: nonSpecialWeaponClasses } = this.splitWeaponClassesBySpecialTypes(
          weaponClasses,
          (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes) ?? false
        );

        // Generate rules for special weapon types (always show, no level difference) - POE2 only
        if (poe2 && filter.showLevelingGearIgnoreLevelDifferenceForSpecialWeaponTypes && specialWeaponClasses.length > 0) {
          weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Normal,
            filterItemClass: specialWeaponClasses.map(c => `"${c}"`).join(' '),
            advancedConditions: new AdvancedConditions({}),
          }), filter, 'Normal Special Leveling Weapons') + '\n\n';
        }

        // Generate rules for non-special weapon types (with level difference)
        if (nonSpecialWeaponClasses.length > 0) {
          let firstRule = true;
          for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
            if (areaLevel < minAreaLevel) { firstRule = false; continue; }
            const dropLevel = areaLevel - filter.showLevelingGearNormalLevelDifference;
            if (dropLevel < 0) continue;

            const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
            firstRule = false;

            weaponRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              filterItemClass: nonSpecialWeaponClasses.map(c => `"${c}"`).join(' '),
              advancedConditions: new AdvancedConditions({ maxAreaLevel: areaLevel, minDropLevel: effectiveMinDropLevel }),
            }), filter, 'Normal Leveling Weapons') + '\n\n';
          }
        }
      }
    }

    let armourRules = '';

    if (filter.showLevelingGearAllMagicRareBoots) {
      const bootsMaxArea = filter.showLevelingGearAllMagicRareBootsMaxAreaLevel;
      const cappedBootsMaxArea = Math.min(bootsMaxArea, maxAreaLevel);
      armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingMagicRareBoots, new CustomRule({
        itemClass: 'Boots',
        advancedConditions: new AdvancedConditions({
          maxAreaLevel: cappedBootsMaxArea,
        })
      }), filter, 'Magic & Rare Leveling Boots', { customLine: '\n  Rarity >= Magic\n  Rarity <= Rare' }, 'gear-leveling-armour-magic-rare-boots') + '\n\n';
    }

    if (filter.showLevelingGearArmour) {
      const classList = is2hWeaponOnly ? ['Body Armours', 'Boots', 'Gloves', 'Helmets'] : ['Body Armours', 'Boots', 'Gloves', 'Helmets', 'Shields'];

      if (poe2 && filter.showLevelingGearArmourDefenceTypes.includes(DefenceType.Evasion)
        && filter.showLevelingGearWeaponClasses.includes(WeaponType.Spears)) {
        classList.push('Bucklers');
      }

      // Build defence exclusions via advanced conditions
      const defenceTypes = filter.showLevelingGearArmourDefenceTypes as DefenceType[];
      const defenceRules = buildDefenceRules(defenceTypes);

      const defenceAdvancedConditions = (defenceRule: DefenceRule, areaLevel?: number, dropLevel?: number, minLinks?: number, minAreaLevelOverride?: number, maxAreaLevelOverride?: number) => new AdvancedConditions({
        minAreaLevel: minAreaLevelOverride,
        maxAreaLevel: maxAreaLevelOverride ?? areaLevel,
        minDropLevel: dropLevel,
        minLinks,
        minArmour: defenceRule.minArmour,
        minEnergyShield: defenceRule.minEnergyShield,
        minEvasion: defenceRule.minEvasion,
      });

      // Generate Excellent Socketed rules (absolute link counts by area band)
      if (poe1 && filter.showLevelingGearExcellentSocketed) {
        const filterItemClass = classList.map(c => `"${c}"`).join(' ');
        for (const band of LEVELING_LINK_BANDS) {
          if (band.excellentLinks === null) continue;
          armourRules += this.emitLinkCountRulesForBand({
            filter,
            band,
            minAreaLevel,
            maxAreaLevel,
            minLinks: band.excellentLinks,
            difference: filter.showLevelingGearExcellentSocketedDifference,
            equipmentType: EquipmentType.OtherGearLevelingExcellentSocketed,
            filterItemClass,
            ruleName: 'Excellent Socketed Leveling Armour',
            alwaysRuleName: 'Leveling Armour Excellent Socketed',
            ruleId: 'gear-leveling-armour-excellent-socketed' as RuleId,
            defenceRules,
            defenceAdvancedConditions,
          });
        }
      }

      // Generate Good Socketed rules (absolute link counts by area band)
      if (poe1 && filter.showLevelingGearOtherSocketed) {
        const filterItemClass = classList.map(c => `"${c}"`).join(' ');
        for (const band of LEVELING_LINK_BANDS) {
          armourRules += this.emitLinkCountRulesForBand({
            filter,
            band,
            minAreaLevel,
            maxAreaLevel,
            minLinks: band.goodLinks,
            difference: filter.showLevelingGearOtherSocketedLevelDifference,
            equipmentType: EquipmentType.OtherGearLevelingGoodSocketed,
            filterItemClass,
            ruleName: 'Good Socketed Leveling Armour',
            alwaysRuleName: 'Leveling Armour Good Socketed',
            ruleId: 'gear-leveling-armour-good-socketed' as RuleId,
            defenceRules,
            defenceAdvancedConditions,
          });
        }
      }

      // Generate Rare rules
      if (filter.showLevelingGearRare) {
        let firstRule = true;
        for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
          if (areaLevel < minAreaLevel) { firstRule = false; continue; }
          const dropLevel = areaLevel - filter.showLevelingGearRareLevelDifference;
          if (dropLevel < 0) continue;

          const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
          firstRule = false;

          const rareArmourEquipmentType = (filter.showVendoringRares && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingRaresStyle
            : EquipmentType.OtherGearLevelingRare;
          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(rareArmourEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Rare,
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel),
            }), filter, 'Rare Leveling Armour', undefined, 'gear-leveling-armour-rare' as RuleId) + '\n\n';
          }
        }
      }

      // Generate Magic rules
      if (filter.showLevelingGearMagic) {
        let firstRule = true;
        for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
          if (areaLevel < minAreaLevel) { firstRule = false; continue; }
          const dropLevel = areaLevel - filter.showLevelingGearMagicLevelDifference;
          if (dropLevel < 0) continue;

          const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
          firstRule = false;

          const magicArmourEquipmentType = (filter.showVendoringMagics && filter.showVendoringRaresType === VendoringGearType.ShowNormalHighlightLevelingRares)
            ? EquipmentType.OtherGearLevelingMagicsStyle
            : EquipmentType.OtherGearLevelingMagic;
          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(magicArmourEquipmentType, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Magic,
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel),
            }), filter, 'Magic Leveling Armour', undefined, 'gear-leveling-armour-magic' as RuleId) + '\n\n';
          }
        }
      }

      // Generate Normal rules
      if (filter.showLevelingGearNormal) {
        let firstRule = true;
        for (let areaLevel = 0; areaLevel <= maxAreaLevel; areaLevel += 5) {
          if (areaLevel < minAreaLevel) { firstRule = false; continue; }
          const dropLevel = areaLevel - filter.showLevelingGearNormalLevelDifference;
          if (dropLevel < 0) continue;

          const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
          firstRule = false;

          for (const defenceRule of defenceRules) {
            armourRules += this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherGearLevelingNormal, new CustomRule({
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              filterItemClass: classList.map(c => `"${c}"`).join(' '),
              advancedConditions: defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel),
            }), filter, 'Normal Leveling Armour', undefined, 'gear-leveling-armour-normal' as RuleId) + '\n\n';
          }
        }
      }
    }

    // Special rule for rares in first 5 areas
    if (filter.showLevelingGear) {
      armourRules += this.ruleFormatter.generateRule(new CustomRule({
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Rare,
        displayOptions: new DisplayOptions(),
        advancedConditions: new AdvancedConditions({ maxAreaLevel: 5 })
      }), filter, 'gear-leveling-rare-first-areas', 'All Rares in First 5 Areas') + '\n\n';
    }

    return weaponRules + '\n\n' + armourRules;
  }

  private emitLinkCountRulesForBand(opts: {
    filter: Filter;
    band: LevelingLinkBand;
    minAreaLevel: number;
    maxAreaLevel: number;
    minLinks: number;
    difference: number | null;
    equipmentType: EquipmentType;
    filterItemClass: string;
    ruleName: string;
    alwaysRuleName: string;
    ruleId?: RuleId;
    defenceRules?: DefenceRule[];
    defenceAdvancedConditions?: (defenceRule: DefenceRule, areaLevel?: number, dropLevel?: number, minLinks?: number, minAreaLevelOverride?: number, maxAreaLevelOverride?: number) => AdvancedConditions;
  }): string {
    const bandMin = Math.max(opts.band.minAreaLevel, opts.minAreaLevel);
    const bandMax = opts.band.maxAreaLevel === null
      ? opts.maxAreaLevel
      : Math.min(opts.band.maxAreaLevel, opts.maxAreaLevel);
    if (bandMin > bandMax) return '';

    let rules = '';

    const emitOne = (advancedConditions: AdvancedConditions, name: string) => {
      rules += this.ruleFormatter.generateEquipmentRule(opts.equipmentType, new CustomRule({
        filterItemClass: opts.filterItemClass,
        advancedConditions,
      }), opts.filter, name, undefined, opts.ruleId) + '\n\n';
    };

    if (opts.difference !== null) {
      let firstRule = true;
      for (let areaLevel = 0; areaLevel <= bandMax; areaLevel += 5) {
        if (areaLevel < bandMin) continue;
        const dropLevel = areaLevel - opts.difference;
        if (dropLevel < 0) continue;

        const effectiveMinDropLevel = firstRule ? undefined : dropLevel;
        firstRule = false;

        if (opts.defenceRules && opts.defenceAdvancedConditions) {
          for (const defenceRule of opts.defenceRules) {
            emitOne(
              opts.defenceAdvancedConditions(defenceRule, areaLevel, effectiveMinDropLevel, opts.minLinks, bandMin),
              opts.ruleName
            );
          }
        } else {
          emitOne(new AdvancedConditions({
            minAreaLevel: bandMin,
            maxAreaLevel: areaLevel,
            minDropLevel: effectiveMinDropLevel,
            minLinks: opts.minLinks,
          }), opts.ruleName);
        }
      }
    } else if (opts.defenceRules && opts.defenceAdvancedConditions) {
      for (const defenceRule of opts.defenceRules) {
        emitOne(
          opts.defenceAdvancedConditions(defenceRule, undefined, undefined, opts.minLinks, bandMin, bandMax),
          opts.alwaysRuleName
        );
      }
    } else {
      emitOne(new AdvancedConditions({
        minAreaLevel: bandMin,
        maxAreaLevel: bandMax,
        minLinks: opts.minLinks,
      }), opts.alwaysRuleName);
    }

    return rules;
  }
}
